import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Coins, Activity, Save, Plus } from "lucide-react";

export default function SaasAdminTab() {
  const [tab, setTab] = useState("rules");
  const [rules, setRules] = useState([]);
  const [usage, setUsage] = useState(null);
  const [manualAmount, setManualAmount] = useState(0);
  const [manualNotes, setManualNotes] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, u] = await Promise.all([
        api.get("/saas/rules"), api.get("/saas/ai-usage?days=30"),
      ]);
      setRules(r.data?.items || []);
      setUsage(u.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveRule = async (feature, cost) => {
    try {
      await api.patch(`/saas/rules/${feature}`, { cost: Number(cost) });
      toast.success("Kural güncellendi"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addPromo = async () => {
    if (!manualAmount || !manualNotes) { toast.error("Miktar ve açıklama zorunlu"); return; }
    try {
      await api.post("/saas/wallet/adjust", { company_id: "default", amount: Number(manualAmount), source: "promo", notes: manualNotes });
      toast.success("Kredi eklendi"); setManualAmount(0); setManualNotes("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="saas-admin-tab">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm bg-muted/40 border border-border">
          <TabsTrigger value="rules" className="rounded-sm"><Coins className="w-3.5 h-3.5 mr-1" /> Kredi Kuralları</TabsTrigger>
          <TabsTrigger value="usage" className="rounded-sm"><Activity className="w-3.5 h-3.5 mr-1" /> AI Kullanım & Maliyet</TabsTrigger>
          <TabsTrigger value="manual" className="rounded-sm"><Plus className="w-3.5 h-3.5 mr-1" /> Manuel Kredi</TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="mt-4">
          <Card className="bg-slate-950/60 border-slate-800 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase text-slate-400 border-b border-slate-800">
                <tr><th className="text-left px-3 py-2">Özellik</th><th className="text-left px-3 py-2">Açıklama</th><th className="text-right px-3 py-2">Kredi Maliyeti</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {rules.map((r) => <RuleRow key={r.feature} rule={r} onSave={saveRule} />)}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="usage" className="mt-4 space-y-3">
          {usage && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Kpi label="Toplam Çağrı (30gün)" value={usage.total.count} />
                <Kpi label="Toplam Token" value={usage.total.tokens} />
                <Kpi label="Gerçek Maliyet" value={`$${usage.total.cost}`} tone="rose" />
                <Kpi label="Harcanan Kredi" value={usage.total.credits} tone="amber" />
              </div>
              <UsageTable title="Model Bazında" rows={usage.by_model} />
              <UsageTable title="Özellik Bazında" rows={usage.by_feature} />
              <UsageTable title="Kullanıcı Bazında" rows={usage.by_user} />
            </>
          )}
        </TabsContent>

        <TabsContent value="manual" className="mt-4 space-y-3">
          <Card className="p-4 bg-slate-950/60 border-slate-800 space-y-3">
            <div className="text-xs text-slate-400">Firmaya (default) promosyon kredisi ekleyin. Her işlem audit log'a yazılır.</div>
            <Input type="number" placeholder="Kredi miktarı" value={manualAmount} onChange={(e) => setManualAmount(e.target.value)}
              className="bg-slate-950 border-slate-700 rounded-sm" data-testid="manual-amount" />
            <Input placeholder="Açıklama (min 3 char)" value={manualNotes} onChange={(e) => setManualNotes(e.target.value)}
              className="bg-slate-950 border-slate-700 rounded-sm" data-testid="manual-notes" />
            <Button className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" onClick={addPromo} data-testid="manual-add">
              <Plus className="w-4 h-4 mr-1" /> Promosyon Ekle
            </Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RuleRow({ rule, onSave }) {
  const [c, setC] = useState(rule.cost);
  return (
    <tr className="border-b border-slate-900" data-testid={`rule-${rule.feature}`}>
      <td className="px-3 py-2 font-mono text-xs text-blue-300">{rule.feature}</td>
      <td className="px-3 py-2 text-xs text-slate-400">{rule.description}</td>
      <td className="px-3 py-2 text-right">
        <Input type="number" value={c} onChange={(e) => setC(e.target.value)} className="bg-slate-950 border-slate-700 rounded-sm w-24 text-right inline-block" />
      </td>
      <td className="px-3 py-2 text-right">
        {Number(c) !== rule.cost && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 rounded-sm h-7" onClick={() => onSave(rule.feature, c)}>
            <Save className="w-3 h-3 mr-1" /> Kaydet
          </Button>
        )}
      </td>
    </tr>
  );
}

function Kpi({ label, value, tone }) {
  const t = tone === "rose" ? "text-rose-300 border-rose-900 bg-rose-950/40" : tone === "amber" ? "text-amber-300 border-amber-900 bg-amber-950/40" : "text-slate-200 border-slate-800 bg-slate-950/60";
  return (
    <div className={`border rounded-sm p-3 ${t}`}>
      <div className="text-[9px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="mt-1 text-lg font-bold font-mono tabular-nums">{typeof value === "number" ? value.toLocaleString("tr-TR") : value}</div>
    </div>
  );
}

function UsageTable({ title, rows }) {
  if (!rows?.length) return null;
  return (
    <Card className="bg-slate-950/60 border-slate-800 p-3">
      <div className="text-xs uppercase tracking-widest text-slate-400 font-mono mb-2">{title}</div>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-slate-500">
          <tr><th className="text-left py-1">Anahtar</th><th className="text-right py-1">Çağrı</th><th className="text-right py-1">Token</th><th className="text-right py-1">Maliyet</th><th className="text-right py-1">Kredi</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-900">
              <td className="py-1 text-slate-200">{r.key}</td>
              <td className="py-1 text-right font-mono">{r.count}</td>
              <td className="py-1 text-right font-mono">{r.tokens.toLocaleString("tr-TR")}</td>
              <td className="py-1 text-right font-mono text-rose-300">${r.cost_usd}</td>
              <td className="py-1 text-right font-mono text-amber-300">{r.credits}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
